import qs from "query-string";
import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { GroupMember } from "@dashboard/devices/types";
import type { Draft, ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";

import { FeatureTypeValue, Workspace, WorkspaceMember } from "../types";

type WorkspaceBuilder = EndpointBuilder<BaseQueryFn, "Workspaces" | "WorkspaceFeatures", "workspacesApi">;

export type GetWorkspacesParams = {
    workspaceId?: number;
    groupId?: number;
    includeTerminated?: boolean;
    includeSettings?: boolean;
};
function getWorkspaces(builder: WorkspaceBuilder) {
    return builder.query<API.Workspace[], GetWorkspacesParams>({
        providesTags: ["Workspaces"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<API.Workspace[]>) => response.model,
        query: async (params) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: base,
                params,
                paramsSerializer: () => qs.stringify(params, { arrayFormat: "index" }),
            };
        },
    });
}

type GetAllWorkspacesParams = {
    pageSize?: number;
    pageIndex?: number;
    sortCriteria?: string;
    sortDirection?: string;
    fromExpirationDate?: string;
    toExpirationDate?: string;
    includeTerminated?: boolean;
    billingType?: "manual" | "paypal" | "stripe";
    featureType?: FeatureTypeValue;
    minFeatureValue?: number;
};
function getAllWorkspaces(builder: WorkspaceBuilder) {
    return builder.query<{ list: API.Workspace[]; count: number }, GetAllWorkspacesParams>({
        providesTags: ["Workspaces"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: PaginatedResponse<API.Workspace>) => ({
            list: response.list,
            count: response.count,
        }),
        query: async (params) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}all`,
                params: {
                    PageIndex: params.pageIndex,
                    PageSize: params.pageSize,
                    SortCriteria: params.sortCriteria,
                    SortDirection: params.sortDirection,
                    FromExpirationDate: params.fromExpirationDate,
                    ToExpirationDate: params.toExpirationDate,
                    IncludeTerminated: params.includeTerminated,
                    BillingType: params.billingType,
                    MinFeatureValue: params.minFeatureValue,
                    FeatureType: params.featureType,
                },
            };
        },
    });
}

export type GetWorkspaceFeaturesParams = {
    id: number;
    /** Defaults to false on the backend, but this client sends true unless overridden.
     * When false, `used` is undefined on every Feature. */
    includeUsage?: boolean;
};

function getWorkspaceFeatures(builder: WorkspaceBuilder) {
    return builder.query<API.Feature[], GetWorkspaceFeaturesParams>({
        providesTags: ["Workspaces"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<API.Feature[]>) => response.model,
        query: async ({ id, includeUsage = true, ...restParams }) => {
            const base = await RouteService.getWorkspacesRoute();
            const params = { ...restParams, includeUsage };
            return {
                url: `${base}${id}/features`,
                params,
                paramsSerializer: () => qs.stringify(params, { arrayFormat: "index" }),
            };
        },
    });
}

function updateWorkspace(builder: WorkspaceBuilder) {
    return builder.mutation<BasicApiResponse, Workspace>({
        invalidatesTags: invalidateOnSuccess("Workspaces"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        query: async ({ id, ...payload }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${id}`,
                method: "PUT",
                data: payload,
            };
        },
    });
}

type MemberUpdatePayload = {
    id: number;
    addedMembers?: Record<string, (GroupMember | WorkspaceMember)["role"]>;
    changedMembers?: Record<string, (GroupMember | WorkspaceMember)["role"]>;
    removedMembers?: string[];
};
function updateWorkspaceMembers(builder: WorkspaceBuilder) {
    return builder.mutation<API.Workspace, MemberUpdatePayload>({
        invalidatesTags: invalidateOnSuccess("Workspaces"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<API.Workspace>) => response.model,
        query: async ({ id, ...payload }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${id}/members`,
                method: "PATCH",
                data: payload,
            };
        },
    });
}

type CancelSubscriptionResponse = { subscriptions?: API.Subscription[] };

function cancelSubscription(builder: WorkspaceBuilder) {
    return builder.mutation<API.Subscription[], number>({
        invalidatesTags: invalidateOnSuccess("Workspaces"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: CancelSubscriptionResponse) => response.subscriptions || [],
        query: async (id) => {
            const base = await RouteService.getSubscriptionsRoute();
            return {
                url: `${base}${id}`,
                method: "PATCH",
                data: { cancelled: true },
            };
        },
    });
}

function claimStarterSubscription(builder: WorkspaceBuilder) {
    return builder.mutation<API.Subscription, void>({
        invalidatesTags: invalidateOnSuccess("Workspaces"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<API.Subscription>) => response.model,
        query: async () => {
            const base = await RouteService.getVouchersRoute();
            return {
                url: base,
                method: "GET",
            };
        },
    });
}

function redeemVoucher(builder: WorkspaceBuilder) {
    return builder.mutation<API.Subscription, string>({
        invalidatesTags: invalidateOnSuccess("Workspaces"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: API.Subscription[]) => response[0],
        query: async (code) => {
            const base = await RouteService.getVouchersRoute();
            return {
                url: base + code,
                method: "POST",
            };
        },
    });
}

type WorkspaceSettingMutationArg = { id: number; name: API.WorkspaceSettingName; value: number };

type GetWorkspacesCachePatch = { undo: () => void };
type GetWorkspacesUpdateQueryDataThunk = ThunkAction<GetWorkspacesCachePatch, unknown, unknown, UnknownAction>;

export type WorkspacesApiForSettingUpdate = {
    util: {
        selectCachedArgsForQuery: (state: unknown, endpointName: "getWorkspaces") => GetWorkspacesParams[];
        updateQueryData: (
            endpoint: "getWorkspaces",
            arg: GetWorkspacesParams,
            updateFn: (draft: Draft<API.Workspace[]>) => void,
        ) => GetWorkspacesUpdateQueryDataThunk;
    };
};

function updateWorkspaceSetting(builder: WorkspaceBuilder, getWorkspacesApi: () => WorkspacesApiForSettingUpdate) {
    return builder.mutation<BasicApiResponse, WorkspaceSettingMutationArg>({
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        query: async ({ id, name, value }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${id}/settings`,
                method: "PUT",
                data: { name, value } satisfies API.WorkspaceSetting,
            };
        },
        async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
            const api = getWorkspacesApi();
            const cachedArgs = api.util.selectCachedArgsForQuery(getState(), "getWorkspaces");

            const patches = cachedArgs
                .filter((queryArg) => queryArg.includeSettings === true)
                .map((queryArg) =>
                    dispatch(
                        api.util.updateQueryData("getWorkspaces", queryArg, (draft) => {
                            const workspace = draft.find((w: API.Workspace) => w.id === arg.id);
                            if (!workspace) {
                                return;
                            }
                            if (!workspace.settings) {
                                workspace.settings = [{ name: arg.name, value: arg.value }];
                                return;
                            }
                            const setting = workspace.settings.find((s: API.WorkspaceSetting) => s.name === arg.name);
                            if (setting) {
                                setting.value = arg.value;
                            } else {
                                workspace.settings.push({ name: arg.name, value: arg.value });
                            }
                        }),
                    ),
                );
            try {
                await queryFulfilled;
            } catch {
                for (const patch of patches) {
                    patch.undo();
                }
            }
        },
    });
}

export default {
    cancelSubscription,
    claimStarterSubscription,
    getAllWorkspaces,
    getWorkspaces,
    getWorkspaceFeatures,
    redeemVoucher,
    updateWorkspace,
    updateWorkspaceMembers,
    updateWorkspaceSetting,
};
